/**
 * Why a New session did not start, in words that say what to fix. The daemon returns a
 * content-free refusal code plus numbers in the (sealed) command result; anything else falls back
 * to the machine's own message. No answer at all is its own case: the machine may be asleep.
 */
import { CommandRejected } from './backend.js'
import { CommandTimeoutError } from './hub.js'

/** How long the dialog waits for the machine's answer before saying so (the hub waits 30 s). */
export const START_REPLY_MS = 20_000

export class StartNoReply extends Error {
  constructor() {
    super('No reply from the machine.')
  }
}

export interface StartFailure {
  title: string
  fix?: string
}

const gb = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1))

export function startFailure(e: unknown, machineName: string): StartFailure {
  if (e instanceof StartNoReply || e instanceof CommandTimeoutError)
    return {
      title: `${machineName} didn’t respond — it may be asleep.`,
      fix: 'Wake it or check that Viko is running there, then start again.',
    }
  if (e instanceof CommandRejected) {
    const r = e.outcome.result as
      | { refused?: unknown; freeGb?: unknown; floorGb?: unknown }
      | undefined
    if (
      r?.refused === 'disk-floor' &&
      typeof r.freeGb === 'number' &&
      typeof r.floorGb === 'number'
    )
      return {
        title: `${machineName} has ${gb(r.freeGb)} GB free, below the ${gb(r.floorGb)} GB floor for new tasks.`,
        fix: 'Free up space, or lower the floor in Settings › Machines.',
      }
    return { title: e.message }
  }
  return { title: (e as Error)?.message || 'Something went wrong.' }
}
